import {
  Avatar,
  FilterOptionsState,
  SxProps,
  createFilterOptions,
} from '@mui/material';
import { ControlledAutocomplete } from 'Components';
import { DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT } from 'Config';
import { TherapySuppliersContext } from 'Context/SuppliersContext/TherapySuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SUPPLIER_RESULT_FILTER } from 'Enum/suppliers';
import { useDictionary, useIsDesktop } from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import { supplierAutoCompleteTitleWidthHandler } from 'Utils/supplierFilterManager';
import {
  ISupplier,
  findResourceIdFromOptions,
  getSupplierResource,
  longestResourceOption,
  transformResourcesToStringArray,
} from 'Utils/suppliersResources';
import { useContext, useEffect, useState } from 'react';

interface ISubCategories {
  autoCompleteSx: SxProps;
  autoCompletePaperSx: SxProps;
}

export const SubCategories = ({
  autoCompleteSx,
  autoCompletePaperSx,
}: ISubCategories) => {
  const { expertise, filters, setFilters, dispatchSuppliers } = useContext(
    TherapySuppliersContext
  );
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage;

  useEffect(() => {
    setSelectedOptions(
      filters.expertise?.map((id: number) => {
        return getSupplierResource(expertise as ISupplier, id);
      }) || []
    );
  }, [filters.expertise, expertise]);

  const isDesktop = useIsDesktop();

  const options = transformResourcesToStringArray(expertise as ISupplier);

  const selectedSubCategoriesHandler = (subCategoryValue: string) => {
    let updatedFilter = { ...filters };
    const updatedSelectedOptions = [...selectedOptions];
    const index = selectedOptions.indexOf(subCategoryValue);
    if (index >= 0) {
      updatedSelectedOptions.splice(index, 1);
      const expertiseOptions = updatedSelectedOptions.map((option: string) => {
        return findResourceIdFromOptions(expertise as ISupplier, option);
      });
      updatedFilter = {
        ...updatedFilter,
        expertise: [...expertiseOptions],
      };
    } else {
      updatedSelectedOptions.push(subCategoryValue);
      const expertiseOptions = updatedSelectedOptions.map((option: string) => {
        return findResourceIdFromOptions(expertise as ISupplier, option);
      });
      updatedFilter = {
        ...updatedFilter,
        expertise: [...expertiseOptions],
      };
    }
    setSelectedOptions(updatedSelectedOptions);
    setFilters(updatedFilter);
    dispatchSuppliers({
      type: SUPPLIER_RESULT_FILTER.Therapy,
      payload: { value: updatedFilter },
    });
  };

  const resetSelectedSubCategoriesHandler = () => {
    const updatedFilter = {
      ...filters,
      expertise: [],
    };
    setSelectedOptions([]);
    setFilters(updatedFilter);
    dispatchSuppliers({
      type: SUPPLIER_RESULT_FILTER.Therapy,
      payload: { value: updatedFilter },
    });
  };

  const subCategoryValueHandler = () => {
    const length = selectedOptions.length;
    if (length === 0) return dictionary.therapy.subSpecialty;
    if (length === 1) return selectedOptions[0];
    else
      return `${dictionary.therapy.chosen} ${selectedOptions.length} ${dictionary.therapy.subSpecialties}`;
  };

  const customFilterOptions = (
    options: string[],
    state: FilterOptionsState<string>
  ) => {
    const inputValue = state.inputValue;

    if (!options.some((option) => option.includes(inputValue)))
      state.inputValue = '';

    return createFilterOptions<string>({
      limit: DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT,
    })(options, state);
  };

  return (
    <ControlledAutocomplete
      label=''
      options={options}
      filterOptions={customFilterOptions}
      isCheckbox
      selectedCheckboxes={selectedOptions}
      value={subCategoryValueHandler()}
      setValue={() => {}}
      onClickCheckbox={selectedSubCategoriesHandler}
      resetSelectedCheckboxes={resetSelectedSubCategoriesHandler}
      style={{
        ...autoCompleteSx,
        '&&& .MuiOutlinedInput-input': {
          p: 0,
          width: `${
            supplierAutoCompleteTitleWidthHandler(subCategoryValueHandler()) + 1
          }ch`,
          cursor: 'pointer',
        },
      }}
      paperSx={{
        ...autoCompletePaperSx,
        minWidth: '305px',
        m: '16px 0 0 -40px',
        pb: '60px',
        width: `${longestResourceOption(options) + 3}ch`,
        boxShadow: 'none',
      }}
      noResultsText={dictionary.noResults}
      endAdornment={
        isDesktop ? (
          <Avatar
            alt=''
            src={AccordionArrowPurple}
            sx={{ width: '30px', height: '30px' }}
          />
        ) : null
      }
      endAdornmentActiveTransformations='rotate(180deg)'
      disableClearIcon
    />
  );
};
